import React, { Fragment, useState, useEffect } from 'react';
import { Nav, NavItem, NavLink, TabContent, TabPane, Form, FormGroup, Label, Input, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Spinner, Button } from 'reactstrap';
import classnames from 'classnames';
import axios from 'axios';
import Swal from 'sweetalert2';

const AttendanceModal = ({ isOpen, onToggle, student, type, onSave, checkInDateFromFilter }) => {
    const token = localStorage.getItem('auth_token');
    const [loading, setLoading] = useState(false);

    const [activeTab, setActiveTab] = useState(type || 'checkIn');
    const [isLateChecked, setIsLateChecked] = useState(false);
    const [selectedOption, setSelectedOption] = useState("");
    const [attendanceStatus, setAttendanceStatus] = useState("attend"); 
    const [isModalOpen, setModalOpen] = useState(false);

    const [studentName, setStudentName] = useState(student?.name || '');
    const [studentId, setStudentId] = useState(student?.student_id || '');
    const [studentSemesterId, setStudentSemesterId] = useState(student?.student_semester_id || '');
    const [checkInDate, setCheckInDate] = useState('');
    const [timeTaken, setTimeTaken] = useState('');
    const [bacaanSuhu, setBacaanSuhu] = useState('36.0');
    const [lateRemarks, setLateRemarks] = useState('');
    const [optionRemarks, setOptionRemarks] = useState('');

    const handleLateToggle = () => {
        setIsLateChecked(!isLateChecked);
    };

    const handleOptionChange = (option) => {
        if (selectedOption === option) {
            setSelectedOption('');
        } else {
            setSelectedOption(option);
        }
    }

    const handleAttendanceChange = (status) => {
        setAttendanceStatus(status);
    }

    const formatDateToYYYYMMDD = (date) => {
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        return yyyy + '-' + mm + '-' + dd;
    }
    
    const formatDateTimeToYYYYMMDDHHMMSS = (dateStr, timeStr) => {
        return `${dateStr} ${timeStr}`;
    };    

    useEffect(() => {
        setActiveTab(type);
        
        const initialCheckInDate = checkInDateFromFilter ? checkInDateFromFilter : new Date();
        setCheckInDate(formatDateToYYYYMMDD(initialCheckInDate));
        
        setStudentId(student?.student_id || '');
        setStudentSemesterId(student?.student_semester_id || '');
    
        const currentTime = initialCheckInDate.toTimeString().split(' ')[0].slice(0, 5);
        setTimeTaken(currentTime);
    }, [type, student, checkInDateFromFilter]);  

    const resetModalState = () => {
        setActiveTab(type);
        setCheckInDate(formatDateToYYYYMMDD(new Date(checkInDateFromFilter)));
        setTimeTaken(timeTaken);
        setBacaanSuhu('');
        setAttendanceStatus("attend");
        setIsLateChecked(false);
        setLateRemarks('');
        setSelectedOption('');
        setOptionRemarks('');
    }; 

    const handleSubmit = async () => {
        const typeMap = {
            'checkIn': 0,
            'breakTime': 1,
            'checkOut': 2
        };
        const typeValue = typeMap[activeTab];

        let currentAttendanceStatus = attendanceStatus;
        if (activeTab === 'breakTime' || activeTab === 'checkOut') {
            currentAttendanceStatus = 'attend';
        }
        const remark = activeTab === 'checkIn' ? lateRemarks : optionRemarks;

        const formData = {
            id: studentSemesterId,
            temperature: bacaanSuhu,
            is_late: isLateChecked ? "1" : "0",
            is_excuse: selectedOption === 'excuses' ? "1" : "0",
            is_overtime: selectedOption === 'overtime' ? "1" : "0",
            timestamp: formatDateTimeToYYYYMMDDHHMMSS(checkInDate, timeTaken),
            type: typeValue,
            remark: remark,
            checkInDate: checkInDate,
            attendanceStatus: attendanceStatus
        };
        setLoading(true);
        try {
            const response = await sendCheckInData(formData);
            console.log("Response from server:", response);
            resetModalState();
            onToggle();
            onSave();
        } catch (error) {
            console.error("Error submitting form", error);
        }
        setLoading(false);
    };
    
    const sendCheckInData = async (data) => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/react/attendances`, 
                data, 
                {
                    headers: { 
                        'Authorization': `Bearer ${token}` 
                    }
                }
            );
    
            if (response.status === 200 || response.status === 201) {
                setLoading(false);
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Data sent successfully!',
                });
            }
        } catch (error) {
            setLoading(false);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error sending data',
            });
        }
    };

    return (
        <Fragment>
            <Modal isOpen={isOpen} title="Attendance Details" centered backdrop="static">
                <ModalHeader>
                    Attendances Details
                </ModalHeader>
                <ModalBody>
                    <Nav tabs>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === 'checkIn' })}
                                onClick={() => setActiveTab('checkIn')}
                                disabled={activeTab !== 'checkIn'}
                            >
                                Check In
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === 'breakTime' })}
                                onClick={() => setActiveTab('breakTime')}
                                disabled={activeTab !== 'breakTime'}
                            >
                                Temperature
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === 'checkOut' })}
                                onClick={() => setActiveTab('checkOut')}
                                disabled={activeTab !== 'checkOut'}
                            >
                                Check Out
                            </NavLink>
                        </NavItem>
                    </Nav>
                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="checkIn">
                            <Form>
                                <FormGroup>
                                    <Label for="studentName">Student Name</Label>
                                    <Input type="text" id="studentName" value={student?.name || ''} readOnly />
                                </FormGroup>
                                <FormGroup tag="fieldset">
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup check>
                                                <Label check>
                                                    <Input
                                                        type="radio"
                                                        name="attendanceStatus"
                                                        value="attend"
                                                        onChange={() => handleAttendanceChange('attend')}
                                                        checked={attendanceStatus === 'attend'}
                                                        className='radio_animated form-check-input'
                                                    />{' '}
                                                    Present
                                                </Label>
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup check>
                                                <Label check>
                                                    <Input
                                                        type="radio"
                                                        name="attendanceStatus"
                                                        value="absent"
                                                        onChange={() => handleAttendanceChange('absent')}
                                                        checked={attendanceStatus === 'absent'}
                                                        className='radio_animated form-check-input'
                                                    />{' '}
                                                    Absent
                                                </Label>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </FormGroup>
                                <FormGroup>
                                    <Label for="checkInDate">Check In Date</Label>
                                    <Input type="date" id="checkInDate" value={checkInDate} onChange={(e) => setCheckInDate(e.target.value)} placeholder="Enter check-in date" />
                                </FormGroup>
                                <FormGroup>
                                    <Label for="timeTaken">Time Taken</Label>
                                    <Input 
                                        type="time" 
                                        id="timeTaken" 
                                        value={timeTaken} 
                                        onChange={(e) => setTimeTaken(e.target.value)} 
                                        placeholder="Enter check-in time" 
                                        disabled={attendanceStatus === 'absent'}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label for="bacaanSuhu">Bacaan Suhu</Label>
                                    <Input 
                                        type="text" 
                                        id="bacaanSuhu" 
                                        value={bacaanSuhu} 
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const regex = /^\d{0,2}(\.\d{0,2})?$/;
                                            if (regex.test(val)) {
                                                if (val === "") {
                                                    setBacaanSuhu(val);
                                                } else if (parseFloat(val) > 50.00) {
                                                    setBacaanSuhu("50.00");
                                                } else if (parseFloat(val) < 20.00 && val.length >= 2 && !val.includes('.')) {
                                                    setBacaanSuhu("20.00");
                                                } else {
                                                    setBacaanSuhu(val);
                                                }
                                            }
                                        }}
                                        placeholder="Enter bacaan suhu" 
                                        disabled={attendanceStatus === 'absent'}
                                        className='mb-2'
                                    />
                                    <div style={{ 
                                            display: 'flex', 
                                            flexDirection: 'row',
                                            overflowX: 'auto', 
                                            width: '100%', 
                                            whiteSpace: 'nowrap'
                                        }}>
                                        {[...Array(11).keys()].map(i => {
                                            const value = (36.0 + i * 0.1).toFixed(1);
                                            return (
                                                <button 
                                                    type="button"
                                                    key={value}
                                                    style={{ 
                                                        margin: '0 2px', 
                                                        padding: '2px 5px', 
                                                        fontSize: '12px' 
                                                    }}
                                                    onClick={() => setBacaanSuhu(value)}
                                                    disabled={attendanceStatus === 'absent'}
                                                    className='btn btn-outline-primary btn-sm'
                                                >
                                                    {value}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </FormGroup>
                                <FormGroup>
                                    <Label for="isLate">Late?</Label>
                                    <Input 
                                        type="checkbox" 
                                        id="isLate" 
                                        onChange={handleLateToggle} 
                                        checked={isLateChecked} 
                                        disabled={attendanceStatus === 'absent'}
                                        className='checkbox_animated form-check-input'
                                    />
                                </FormGroup>
                                {isLateChecked && attendanceStatus !== 'absent' && (
                                    <FormGroup>
                                        <Label for="lateRemarks">Late Remarks</Label>
                                        <Input 
                                            type="textarea" 
                                            id="lateRemarks" 
                                            placeholder="Enter remarks" 
                                            onChange={(e) => setLateRemarks(e.target.value)} 
                                            value={lateRemarks}
                                            disabled={attendanceStatus === 'absent'}
                                        />
                                    </FormGroup>
                                )}
                            </Form>
                        </TabPane>
                        <TabPane tabId="breakTime">
                            <Form>
                                <FormGroup>
                                    <Label for="studentName">Student Name</Label>
                                    <Input type="text" id="studentName" value={student?.name || ''} readOnly />
                                </FormGroup>
                                <FormGroup>
                                    <Label for="checkInDate">Check In Date</Label>
                                    <Input type="date" id="checkInDate" value={checkInDate} onChange={(e) => setCheckInDate(e.target.value)} placeholder="Enter check-in date" />
                                </FormGroup>
                                <FormGroup>
                                    <Label for="timeTaken">Time Taken</Label>
                                    <Input type="time" id="timeTaken" value={timeTaken} onChange={(e) => setTimeTaken(e.target.value)} placeholder="Enter check-in time" />
                                </FormGroup>
                                <FormGroup>
                                    <Label for="bacaanSuhu">Bacaan Suhu</Label>
                                    <Input 
                                        type="text" 
                                        id="bacaanSuhu" 
                                        value={bacaanSuhu}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const regex = /^\d{0,2}(\.\d{0,2})?$/;
                                            if (regex.test(val)) {
                                                if (val === "") {
                                                    setBacaanSuhu(val);
                                                } else if (parseFloat(val) > 50.00) {
                                                    setBacaanSuhu("50.00");
                                                } else if (parseFloat(val) < 20.00 && val.length >= 2 && !val.includes('.')) {
                                                    setBacaanSuhu("20.00");
                                                } else {
                                                    setBacaanSuhu(val);
                                                }
                                            }
                                        }}
                                        placeholder="Enter bacaan suhu" 
                                        className='mb-2'
                                    />
                                    <div style={{ 
                                            display: 'flex', 
                                            flexDirection: 'row',
                                            overflowX: 'auto', 
                                            width: '100%', 
                                            whiteSpace: 'nowrap'
                                        }}>
                                        {[...Array(11).keys()].map(i => {
                                            const value = (36.0 + i * 0.1).toFixed(1);
                                            return (
                                                <button 
                                                    type="button"
                                                    key={value}
                                                    style={{ 
                                                        margin: '0 2px', 
                                                        padding: '2px 5px', 
                                                        fontSize: '12px' 
                                                    }}
                                                    onClick={() => setBacaanSuhu(value)}
                                                    className='btn btn-outline-primary btn-sm'
                                                >
                                                    {value}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </FormGroup>
                            </Form>
                        </TabPane>
                        <TabPane tabId="checkOut">
                            <Form>
                                <FormGroup>
                                    <Label for="studentName">Student Name</Label>
                                    <Input type="text" id="studentName" value={student?.name || ''} readOnly />
                                </FormGroup>
                                <FormGroup>
                                    <Label for="checkInDate">Check In Date</Label>
                                    <Input type="date" id="checkInDate" value={checkInDate} onChange={(e) => setCheckInDate(e.target.value)} placeholder="Enter check-in date" />
                                </FormGroup>
                                <FormGroup>
                                    <Label for="timeTaken">Time Taken</Label>
                                    <Input type="time" id="timeTaken" value={timeTaken} onChange={(e) => setTimeTaken(e.target.value)} placeholder="Enter check-in time" />
                                </FormGroup>
                                <FormGroup>
                                    <Label for="bacaanSuhu">Bacaan Suhu</Label>
                                    <Input 
                                        type="text" 
                                        id="bacaanSuhu" 
                                        value={bacaanSuhu}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const regex = /^\d{0,2}(\.\d{0,2})?$/;
                                            if (regex.test(val)) {
                                                if (val === "") {
                                                    setBacaanSuhu(val);
                                                } else if (parseFloat(val) > 50.00) {
                                                    setBacaanSuhu("50.00");
                                                } else if (parseFloat(val) < 20.00 && val.length >= 2 && !val.includes('.')) {
                                                    setBacaanSuhu("20.00");
                                                } else {
                                                    setBacaanSuhu(val);
                                                }
                                            }
                                        }}
                                        placeholder="Enter bacaan suhu" 
                                        className='mb-2'
                                    />
                                    <div style={{ 
                                            display: 'flex', 
                                            flexDirection: 'row',
                                            overflowX: 'auto', 
                                            width: '100%', 
                                            whiteSpace: 'nowrap'
                                        }}>
                                        {[...Array(11).keys()].map(i => {
                                            const value = (36.0 + i * 0.1).toFixed(1);
                                            return (
                                                <button 
                                                    type="button"
                                                    key={value}
                                                    style={{ 
                                                        margin: '0 2px', 
                                                        padding: '2px 5px', 
                                                        fontSize: '12px' 
                                                    }}
                                                    onClick={() => setBacaanSuhu(value)}
                                                    className='btn btn-outline-primary btn-sm'
                                                >
                                                    {value}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </FormGroup>
                                <FormGroup check inline>
                                    <Label check>
                                        <Input 
                                            type="checkbox" 
                                            name="option" 
                                            value="excuses"
                                            onChange={() => handleOptionChange('excuses')} 
                                            checked={selectedOption === 'excuses'} 
                                            className='checkbox_animated form-check-input'
                                        />{' '}
                                        Excuse
                                    </Label>
                                </FormGroup>
                                <FormGroup check inline>
                                    <Label check>
                                        <Input 
                                            type="checkbox" 
                                            name="option" 
                                            value="overtime"
                                            onChange={() => handleOptionChange('overtime')} 
                                            checked={selectedOption === 'overtime'} 
                                            className='checkbox_animated form-check-input'
                                        />{' '}
                                        Overtime
                                    </Label>
                                </FormGroup>

                                {(selectedOption === 'excuses' || selectedOption === 'overtime') && (
                                    <FormGroup>
                                        <Label for="optionRemarks">Remarks</Label>
                                        <Input type="textarea" id="optionRemarks" placeholder="Enter remarks" onChange={(e) => setOptionRemarks(e.target.value)} value={optionRemarks} />
                                    </FormGroup>
                                )}
                            </Form>
                        </TabPane>
                    </TabContent>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={onToggle}>Cancel</Button>
                    <Button color="primary" onClick={handleSubmit} disabled={loading}>
                        {loading ? <Spinner size="sm" color="light" /> : "Save"}
                    </Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default AttendanceModal;